import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, X, Layers, Users, Sparkles, Zap, Mail, MessageCircle,
  PhoneCall, Server, ShieldCheck, Wallet, Gift, RefreshCw, Info, ChevronDown, Loader2, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { Brand } from "@/components/Brand";

const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

const Overline = ({ children }) => (
  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B3D91]">{children}</span>
);

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const rc = (n) => `${Number(n).toLocaleString("en-IN")}`;
const COMM_CHANNELS = ["email", "whatsapp", "sms", "voice"];

const CTA_WORDING = {
  PAYG: { label: "Start 3 Months PAYG", sub: "New users only" },
  Essential: { label: "Get Essential" },
  Starter: { label: "Get Starter" },
  Growth: { label: "Get Growth" },
  Professional: { label: "Get Professional" },
};

const CHANNEL_META = {
  email: { label: "Email", icon: Mail },
  whatsapp: { label: "WhatsApp", icon: MessageCircle },
  sms: { label: "SMS", icon: MessageCircle },
  voice: { label: "Voice / IVR", icon: PhoneCall },
};

const AvailabilityPill = ({ status }) => {
  const live = status === "LIVE";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${
        live ? "bg-[#16A34A]/10 text-[#16A34A]" : "bg-amber-100 text-amber-700"
      }`}
      data-testid={`availability-${status.toLowerCase()}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-[#16A34A]" : "bg-amber-500"}`} />
      {live ? "Live" : "Coming Soon"}
    </span>
  );
};

const FaqRow = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white" data-testid={`pricing-faq-${idx}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid={`pricing-faq-toggle-${idx}`}
        aria-expanded={open}
      >
        <span className="font-semibold text-slate-900" style={JK}>{q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-[#0B3D91] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed" data-testid={`pricing-faq-body-${idx}`}>{a}</div>}
    </div>
  );
};

export default function Pricing() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    api
      .get("/public/pricing")
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const plans = data?.plans || [];
  const rcPacks = (data?.rc_packs || []).filter((p) => p.enabled);
  const rateCard = data?.rate_card || { availability: {}, rates: [] };
  const annualMonthsFree = data?.annual_months_free ?? 2;
  const rcValidity = data?.rc_validity_months ?? 12;

  const goSignup = (key) => navigate(`/signup?plan=${encodeURIComponent(key)}${period === "annual" ? "&billing=annual" : ""}`);

  // Group communication rates by channel (exclude platform/AI/automation actions -> shown separately)
  const commChannels = COMM_CHANNELS;
  const ratesByChannel = useMemo(() => {
    const g = {};
    for (const ch of COMM_CHANNELS) g[ch] = [];
    for (const rrow of (data?.rate_card?.rates || [])) {
      if (rrow.channel && COMM_CHANNELS.includes(rrow.channel)) g[rrow.channel].push(rrow);
    }
    return g;
  }, [data]);

  const platformRates = (rateCard.rates || []).filter((r) => !r.channel);

  // ---- Loading ----
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4" data-testid="pricing-loading">
        <Loader2 className="h-8 w-8 text-[#0B3D91] animate-spin" />
        <p className="text-slate-500 text-sm">Loading pricing…</p>
      </div>
    );
  }

  // ---- Error ----
  if (error || !data) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="pricing-error">
        <AlertTriangle className="h-10 w-10 text-amber-500" />
        <h2 className="text-xl font-bold text-slate-900" style={JK}>We couldn't load pricing right now</h2>
        <p className="text-slate-500 text-sm max-w-md">Please check your connection and try again.</p>
        <button onClick={load} className="mt-2 inline-flex items-center gap-2 bg-[#0B3D91] text-white rounded-none px-6 py-3 font-semibold hover:bg-[#0a347d] transition-colors" data-testid="pricing-retry">
          <RefreshCw className="h-4 w-4" /> Retry
        </button>
        <Link to="/" className="text-sm text-slate-500 underline hover:text-slate-800" data-testid="pricing-error-home">Back to home</Link>
      </div>
    );
  }

  // ---- Empty ----
  if (!plans.length) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="pricing-empty">
        <Info className="h-10 w-10 text-slate-400" />
        <h2 className="text-xl font-bold text-slate-900" style={JK}>Pricing is being updated</h2>
        <p className="text-slate-500 text-sm max-w-md">Our plans are being refreshed. Please check back shortly or talk to our team.</p>
        <button onClick={() => navigate("/signup")} className="mt-2 bg-[#0B3D91] text-white rounded-none px-6 py-3 font-semibold hover:bg-[#0a347d] transition-colors" data-testid="pricing-empty-cta">Get Started</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="pricing-page">
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-brand-logo">
            <Brand iconSize={32} textClass="text-lg" style={JK} />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/#features" className="text-sm font-medium text-slate-600 hover:text-slate-950 transition-colors" data-testid="pricing-nav-features">Features</Link>
            <span className="text-sm font-semibold text-[#0B3D91]" data-testid="pricing-nav-active">Pricing</span>
            <Link to="/#faq" className="text-sm font-medium text-slate-600 hover:text-slate-950 transition-colors" data-testid="pricing-nav-faq">FAQ</Link>
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/signin")} className="hidden sm:inline-flex text-sm font-semibold text-slate-700 hover:text-[#0B3D91] transition-colors px-3 py-2" data-testid="pricing-nav-login">Login</button>
            <button onClick={() => navigate("/signup")} className="inline-flex bg-[#0B3D91] text-white rounded-none px-4 py-2.5 text-sm font-semibold hover:bg-[#0a347d] transition-colors" data-testid="pricing-nav-signup">Start Free</button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative py-16 lg:py-24 border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_0%,rgba(11,61,145,0.06),transparent_45%),radial-gradient(circle_at_90%_10%,rgba(22,163,74,0.06),transparent_40%)]" />
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <Overline>Pricing</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#0B3D91] leading-none" style={JK}>
            One platform. <span className="text-[#16A34A]">Fair, usage-based</span> pricing.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Pick a RecoverOS plan for your team. Every plan includes <strong>Free Monthly RC</strong> for communications — top up anytime with Fusion RC. No hidden fees.
          </p>

          {/* Monthly / Annual toggle */}
          <div className="mt-8 inline-flex items-center gap-1 border border-slate-200 bg-slate-50 p-1" data-testid="billing-toggle">
            <button
              onClick={() => setPeriod("monthly")}
              className={`px-5 py-2 text-sm font-semibold transition-colors ${period === "monthly" ? "bg-white text-[#0B3D91] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid="billing-toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setPeriod("annual")}
              className={`px-5 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${period === "annual" ? "bg-white text-[#0B3D91] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid="billing-toggle-annual"
            >
              Annual
              <span className="bg-[#16A34A] text-white text-[10px] font-bold uppercase px-1.5 py-0.5 tracking-wide">{annualMonthsFree} Months Free</span>
            </button>
          </div>
        </div>
      </section>

      {/* PLAN CARDS */}
      <section className="py-14 lg:py-20 bg-slate-50 border-b border-slate-200" id="plans">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 items-stretch">
            {plans.map((p, i) => {
              const isPayg = p.is_payg;
              const isAnnual = period === "annual" && p.annual_available !== false && !isPayg;
              const priceNum = isAnnual ? p.price_annual : p.price;
              const cta = CTA_WORDING[p.key] || { label: p.cta || "Get Started" };
              const testKey = p.key.toLowerCase();
              return (
                <FadeUp key={p.key} delay={i * 0.05} className={p.popular ? "xl:-my-2 z-10" : ""}>
                  <div
                    className={`relative bg-white h-full flex flex-col p-6 ${p.popular ? "border-2 border-[#16A34A] shadow-2xl ring-1 ring-[#16A34A]/20" : "border border-slate-200"}`}
                    data-testid={`plan-card-${testKey}`}
                  >
                    {p.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#16A34A] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 whitespace-nowrap">★ Recommended</span>
                    )}
                    <div className={`text-sm font-bold uppercase tracking-wider ${p.popular ? "text-[#16A34A]" : "text-slate-500"}`} style={JK}>{p.name}</div>

                    {/* price */}
                    <div className="mt-3 min-h-[64px]">
                      {isPayg ? (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-black text-slate-950" style={JK}>₹0</span>
                            <span className="text-sm text-slate-500">for 3 months</span>
                          </div>
                          <p className="mt-1 text-xs font-semibold text-[#0B3D91]">New users only</p>
                        </>
                      ) : (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className={`font-black text-slate-950 ${p.popular ? "text-5xl" : "text-4xl"}`} style={JK}>{inr(priceNum)}</span>
                            <span className="text-sm text-slate-500">/{isAnnual ? "yr" : "mo"}</span>
                          </div>
                          {isAnnual ? (
                            <p className="mt-1 text-xs font-semibold text-[#16A34A]" data-testid={`plan-annual-note-${testKey}`}>
                              {p.annual_savings_label || `Save ${inr(p.price * 12 - p.price_annual)}/year · ${annualMonthsFree} months free`}
                            </p>
                          ) : (
                            p.price_annual > 0 && <p className="mt-1 text-xs text-slate-400">or {inr(p.price_annual)}/yr — save {annualMonthsFree} months</p>
                          )}
                        </>
                      )}
                    </div>

                    {/* limits */}
                    <div className="mt-4 flex flex-col gap-1.5 border-t border-slate-100 pt-4">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Layers className="h-4 w-4 text-[#0B3D91]" /> {Number(p.account_limit).toLocaleString("en-IN")} accounts</span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Users className="h-4 w-4 text-[#0B3D91]" /> {p.user_limit} {p.user_limit > 1 ? "users" : "user"}</span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#16A34A]" data-testid={`plan-freerc-${testKey}`}><Gift className="h-4 w-4" /> Up to {p.free_rc_monthly} Free RC/month</span>
                      {isPayg && <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500" data-testid="plan-emailcomms-payg"><Mail className="h-3.5 w-3.5" /> Up to 250 email communications</span>}
                    </div>

                    {/* features */}
                    <ul className="mt-4 space-y-2 flex-1 border-t border-slate-100 pt-4">
                      {(p.features || []).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[13px] text-slate-600"><Check className="h-4 w-4 text-[#16A34A] shrink-0 mt-0.5" />{f}</li>
                      ))}
                      {(p.excluded || []).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[13px] text-slate-400"><X className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />{f}</li>
                      ))}
                    </ul>

                    {/* cta */}
                    <button
                      onClick={() => goSignup(p.key)}
                      className={`mt-5 w-full rounded-none px-4 py-3 font-semibold transition-colors ${p.popular ? "bg-[#16A34A] text-white hover:bg-[#138a3e]" : "bg-[#0B3D91] text-white hover:bg-[#0a347d]"}`}
                      data-testid={`plan-cta-${testKey}`}
                    >
                      {cta.label}
                    </button>
                    {cta.sub && <p className="mt-2 text-center text-[11px] text-slate-400">{cta.sub}</p>}
                  </div>
                </FadeUp>
              );
            })}
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">Prices in INR, exclusive of applicable taxes. Paid plans are not free — the Intro PAYG tier is a 3-month starter for new tenants only.</p>
        </div>
      </section>

      {/* HOW PRICING WORKS */}
      <section className="py-16 lg:py-24 bg-white" id="how-pricing-works">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="text-center max-w-2xl mx-auto">
            <Overline>How it fits together</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>Three simple parts to RecoverOS pricing</h2>
            <p className="mt-3 text-slate-500">Your subscription runs the platform. RC pays for outbound communications. They are separate — so you always know what you're paying for.</p>
          </FadeUp>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { icon: Wallet, title: "1 · SaaS Subscription", body: "A fixed monthly (or annual) fee for your plan — accounts, users, dashboards, Money Stuck Analyzer™, automation and AI. This is your platform access.", tag: "Fixed fee" },
              { icon: Gift, title: "2 · Free Monthly RC", body: "Every plan includes up to a set amount of Free RecoverOS Credits each month for communications. Unused free RC does not roll over — it's a monthly allowance, not a lump-sum grant.", tag: "Included allowance" },
              { icon: Zap, title: "3 · Fusion RC + Usage", body: "Send more than your free allowance? Top up with Fusion RC recharge packs (with bonus RC). Each email / message / call consumes RC at transparent per-action rates.", tag: "Pay as you grow" },
            ].map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.06}>
                <div className="h-full border border-slate-200 bg-white p-7 hover:border-[#0B3D91]/40 transition-colors" data-testid={`pricing-part-${i + 1}`}>
                  <div className="inline-flex h-11 w-11 items-center justify-center bg-[#0B3D91]/[0.06] text-[#0B3D91]"><c.icon className="h-5 w-5" /></div>
                  <div className="mt-4 text-xs font-bold uppercase tracking-wider text-[#16A34A]">{c.tag}</div>
                  <h3 className="mt-1 text-lg font-bold text-slate-950" style={JK}>{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          {/* RC definition callout */}
          <FadeUp delay={0.1}>
            <div className="mt-6 border border-[#0B3D91]/20 bg-[#0B3D91]/[0.03] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="rc-definition">
              <div className="inline-flex h-10 w-10 items-center justify-center bg-[#0B3D91] text-white shrink-0"><Info className="h-5 w-5" /></div>
              <p className="text-sm text-slate-700 leading-relaxed">
                <strong className="text-[#0B3D91]">RC = RecoverOS Credits.</strong> RC is the single unit used across all communications and AI actions (1 RC = {inr(data.rc_value_inr ?? 1)}). One email might cost a fraction of an RC — you only spend RC when you actually send something. As a guide, Intro PAYG's Free RC covers up to 250 email communications a month.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FUSION RC */}
      <section className="py-16 lg:py-24 bg-slate-50 border-y border-slate-200" id="fusion-rc">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="text-center max-w-2xl mx-auto">
            <Overline>Fusion RC Recharge</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>Top up credits whenever you need them</h2>
            <p className="mt-3 text-slate-500">Buy Fusion RC packs on top of your Free Monthly RC. Bigger packs earn bonus RC. Purchased RC is valid for {rcValidity} months.</p>
          </FadeUp>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rcPacks.map((pack, i) => (
              <FadeUp key={pack.pack_key} delay={i * 0.04}>
                <div className={`relative bg-white h-full p-6 flex flex-col ${pack.popular ? "border-2 border-[#16A34A] shadow-lg" : "border border-slate-200"}`} data-testid={`rc-pack-${pack.pack_key}`}>
                  {pack.popular && <span className="absolute -top-3 left-6 bg-[#16A34A] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">Best Value</span>}
                  <div className="flex items-baseline justify-between">
                    <span className="text-2xl font-black text-slate-950" style={JK}>{inr(pack.price_inr)}</span>
                    {pack.bonus_rc > 0 && <span className="text-[11px] font-bold uppercase tracking-wide bg-[#16A34A]/10 text-[#16A34A] px-2 py-1">{pack.bonus_label}</span>}
                  </div>
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-black text-[#0B3D91]" style={JK}>{rc(pack.total_rc)}</span>
                      <span className="text-sm font-semibold text-slate-500">RC</span>
                    </div>
                    {pack.bonus_rc > 0 ? (
                      <p className="mt-1 text-xs text-slate-500">{rc(pack.base_rc)} base + <span className="font-semibold text-[#16A34A]">{rc(pack.bonus_rc)} bonus</span></p>
                    ) : (
                      <p className="mt-1 text-xs text-slate-400">{rc(pack.base_rc)} RC · no bonus</p>
                    )}
                  </div>
                  <button onClick={() => navigate("/signup")} className="mt-auto pt-5 text-sm font-semibold text-[#0B3D91] inline-flex items-center gap-1.5 hover:gap-2.5 transition-all" data-testid={`rc-pack-cta-${pack.pack_key}`}>
                    Available after signup <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </FadeUp>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">Bonus RC shown as base + bonus (e.g. {inr(10000)} → {rc(10500)} RC). Recharge is managed inside your workspace once you sign up.</p>
        </div>
      </section>

      {/* BYOP vs MANAGED */}
      <section className="py-16 lg:py-24 bg-white" id="byop">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="text-center max-w-2xl mx-auto">
            <Overline>Two ways to send</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>Bring your own provider — or let us handle it</h2>
            <p className="mt-3 text-slate-500">Communication rates depend on how messages are delivered. You choose the model that fits your setup.</p>
          </FadeUp>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <FadeUp>
              <div className="h-full border border-slate-200 bg-white p-8" data-testid="byop-card">
                <div className="inline-flex h-11 w-11 items-center justify-center bg-[#0B3D91]/[0.06] text-[#0B3D91]"><Server className="h-5 w-5" /></div>
                <h3 className="mt-4 text-xl font-bold text-slate-950" style={JK}>BYOP — Bring Your Own Provider</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">Connect your own email / messaging accounts (e.g. your own sending domain). You pay your provider directly for delivery, and RecoverOS charges only a small platform RC rate per action.</p>
                <ul className="mt-5 space-y-2">
                  {["Lowest RC rates per message", "Use your existing sender reputation", "You manage provider billing directly"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-[#16A34A] shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.06}>
              <div className="h-full border-2 border-[#0B3D91]/20 bg-[#0B3D91]/[0.02] p-8" data-testid="managed-card">
                <div className="inline-flex h-11 w-11 items-center justify-center bg-[#0B3D91] text-white"><ShieldCheck className="h-5 w-5" /></div>
                <h3 className="mt-4 text-xl font-bold text-slate-950" style={JK}>RecoverOS Billing (Managed)</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">We handle delivery end-to-end using RecoverOS infrastructure. One combined RC rate covers both delivery and platform — nothing else to set up or manage.</p>
                <ul className="mt-5 space-y-2">
                  {["Zero provider setup — start sending instantly", "Single RC rate covers everything", "Compliant sending managed for you"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-[#16A34A] shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* COMMUNICATION RATE CARD */}
      <section className="py-16 lg:py-24 bg-slate-50 border-y border-slate-200" id="rates">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="text-center max-w-2xl mx-auto">
            <Overline>Communication Rates</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>Transparent per-action RC rates</h2>
            <p className="mt-3 text-slate-500">You spend RC only when you communicate. Rates shown per action in RC. Email is live today; other channels are rolling out.</p>
          </FadeUp>

          <div className="mt-12 space-y-6">
            {commChannels.map((ch) => {
              const meta = CHANNEL_META[ch];
              const rows = ratesByChannel[ch] || [];
              if (!rows.length) return null;
              const Icon = meta.icon;
              const status = rateCard.availability?.[ch] || "COMING_SOON";
              return (
                <FadeUp key={ch}>
                  <div className={`bg-white border ${status === "LIVE" ? "border-[#16A34A]/40" : "border-slate-200"}`} data-testid={`rate-channel-${ch}`}>
                    <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="inline-flex h-9 w-9 items-center justify-center bg-[#0B3D91]/[0.06] text-[#0B3D91]"><Icon className="h-4.5 w-4.5" /></div>
                        <span className="font-bold text-slate-950" style={JK}>{meta.label}</span>
                      </div>
                      <AvailabilityPill status={status} />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                            <th className="px-6 py-3 font-semibold">Action / Type</th>
                            <th className="px-4 py-3 font-semibold">Delivery</th>
                            <th className="px-6 py-3 font-semibold text-right">Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, ri) => (
                            <tr key={ri} className="border-b border-slate-50 last:border-0" data-testid={`rate-row-${ch}-${ri}`}>
                              <td className="px-6 py-3 text-slate-700">
                                {r.message_type ? <span className="capitalize">{r.message_type}</span> : r.action_code.replace(/_/g, " ").toLowerCase()}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold ${r.billing_mode === "BYOP" ? "bg-slate-100 text-slate-600" : "bg-[#0B3D91]/[0.08] text-[#0B3D91]"}`}>
                                  {r.billing_mode === "BYOP" ? "BYOP" : "Managed"}
                                </span>
                              </td>
                              <td className="px-6 py-3 text-right font-semibold text-slate-900 tabular-nums">
                                {r.customer_rate_rc != null ? `${r.customer_rate_rc} RC${r.billing_unit === "minute" ? "/min" : ""}` : "On request"}
                              </td>
                            </tr>
                          ))}
                          {ch === "whatsapp" && (
                            <tr className="border-b border-slate-50" data-testid="rate-row-whatsapp-auth-managed">
                              <td className="px-6 py-3 text-slate-700">Authentication</td>
                              <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 text-[11px] font-semibold bg-[#0B3D91]/[0.08] text-[#0B3D91]">Managed</span></td>
                              <td className="px-6 py-3 text-right font-semibold text-slate-400">On request</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              );
            })}

            {/* Platform / AI actions */}
            {platformRates.length > 0 && (
              <FadeUp>
                <div className="bg-white border border-slate-200" data-testid="rate-channel-platform">
                  <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
                    <div className="inline-flex h-9 w-9 items-center justify-center bg-[#16A34A]/10 text-[#16A34A]"><Sparkles className="h-4.5 w-4.5" /></div>
                    <span className="font-bold text-slate-950" style={JK}>Platform &amp; AI actions</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody>
                        {Array.from(new Map(platformRates.map((r) => [r.action_code, r])).values()).map((r, ri) => (
                          <tr key={ri} className="border-b border-slate-50 last:border-0" data-testid={`rate-platform-${ri}`}>
                            <td className="px-6 py-3 text-slate-700 capitalize">{r.action_code.replace(/_/g, " ").toLowerCase()}</td>
                            <td className="px-6 py-3 text-right font-semibold text-slate-900 tabular-nums">{r.customer_rate_rc === 0 ? "Free" : `${r.customer_rate_rc} RC`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </FadeUp>
            )}
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">Rates are indicative and may be updated. WhatsApp, SMS and Voice are marked Coming Soon until fully operational in your region.</p>
        </div>
      </section>

      {/* FEATURE COMPARISON */}
      <section className="py-16 lg:py-24 bg-white" id="compare">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp className="text-center max-w-2xl mx-auto">
            <Overline>Compare plans</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>Full plan comparison</h2>
            <p className="mt-3 text-slate-500">Limits and included allowances across every plan.</p>
          </FadeUp>
          <FadeUp delay={0.05}>
            <div className="mt-10 overflow-x-auto border border-slate-200">
              <table className="w-full text-sm min-w-[720px]" data-testid="compare-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-left">
                    <th className="px-5 py-4 font-bold text-slate-950" style={JK}>Plan</th>
                    <th className="px-4 py-4 font-semibold text-slate-600">Monthly</th>
                    <th className="px-4 py-4 font-semibold text-slate-600">Annual</th>
                    <th className="px-4 py-4 font-semibold text-slate-600">Accounts</th>
                    <th className="px-4 py-4 font-semibold text-slate-600">Users</th>
                    <th className="px-4 py-4 font-semibold text-slate-600">Free RC / mo</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => (
                    <tr key={p.key} className={`border-b border-slate-100 last:border-0 ${p.popular ? "bg-[#16A34A]/[0.03]" : ""}`} data-testid={`compare-row-${p.key.toLowerCase()}`}>
                      <td className="px-5 py-4">
                        <span className="font-bold text-slate-950" style={JK}>{p.name}</span>
                        {p.popular && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-[#16A34A]">Recommended</span>}
                        {p.is_payg && <div className="text-[11px] text-slate-400">New users · 3 months</div>}
                      </td>
                      <td className="px-4 py-4 font-semibold text-slate-900">{p.is_payg ? "₹0" : `${inr(p.price)}/mo`}</td>
                      <td className="px-4 py-4 text-slate-600">{p.is_payg || p.annual_available === false ? "—" : `${inr(p.price_annual)}/yr`}</td>
                      <td className="px-4 py-4 text-slate-600">{Number(p.account_limit).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-4 text-slate-600">{p.user_limit}</td>
                      <td className="px-4 py-4 text-[#16A34A] font-semibold">Up to {p.free_rc_monthly}</td>
                      <td className="px-5 py-4 text-right">
                        <button onClick={() => goSignup(p.key)} className="text-sm font-semibold text-[#0B3D91] hover:underline inline-flex items-center gap-1" data-testid={`compare-cta-${p.key.toLowerCase()}`}>
                          {(CTA_WORDING[p.key]?.label) || "Choose"} <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <FadeUp className="text-center">
            <Overline>FAQ</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>Pricing questions</h2>
          </FadeUp>
          <div className="mt-10 space-y-3">
            {[
              { q: "What is RC (RecoverOS Credits)?", a: "RC is the single credit unit used for all communications and AI actions in RecoverOS (1 RC = ₹1). You spend RC only when you send an email/message or run an AI action — nothing is deducted when you're idle." },
              { q: "What does 'Up to X Free RC/month' mean?", a: "Every plan includes a monthly allowance of free RC for communications — for Intro PAYG that's up to 50 Free RC/month, roughly up to 250 email communications. It's a per-month allowance (not a guaranteed lump sum you keep) — unused free RC does not carry over to the next month. Need more? Top up with Fusion RC anytime." },
              { q: "What is BYOP?", a: "BYOP means 'Bring Your Own Provider'. If you connect your own sending accounts, you pay your provider for delivery and RecoverOS charges a lower platform RC rate. Prefer no setup? Choose RecoverOS Billing (Managed) where one RC rate covers everything." },
              { q: "How long is purchased Fusion RC valid?", a: `Purchased Fusion RC is valid for ${rcValidity} months from the date of recharge. Your Free Monthly RC is separate and resets each month.` },
              { q: "Is the Intro PAYG plan free forever?", a: "No. Intro PAYG is a ₹0, 3-month starter for new tenants only (100 accounts, 1 user, up to 250 email communications with up to 50 Free RC/month). After 3 months you choose a paid plan to continue." },
              { q: "Do annual plans really include 2 months free?", a: `Yes. Annual billing is priced at ${annualMonthsFree} months free compared to paying monthly for the same plan.` },
            ].map((f, i) => (<FaqRow key={i} q={f.q} a={f.a} idx={i} />))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 lg:py-24 bg-[#0B3D91]">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white leading-none" style={JK}>Find your stuck money today.</h2>
          <p className="mt-4 text-blue-100 text-base sm:text-lg">Start free — upload your portfolio and see recoverable amounts in minutes.</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => navigate("/signup")} className="w-full sm:w-auto bg-[#16A34A] text-white rounded-none px-8 py-4 font-bold text-lg hover:bg-[#138a3e] transition-colors inline-flex items-center justify-center gap-2" data-testid="pricing-final-signup">
              Start with Intro PAYG <ArrowRight className="h-5 w-5" />
            </button>
            <button onClick={() => window.dispatchEvent(new Event("recoveros:open-chat"))} className="w-full sm:w-auto border-2 border-white/40 text-white rounded-none px-8 py-4 font-semibold text-lg hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2" data-testid="pricing-final-chat">
              <MessageCircle className="h-5 w-5" /> Talk to us
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-slate-400 py-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-footer-logo"><Brand dark iconSize={28} textClass="text-base" style={JK} /></Link>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/" className="hover:text-white transition-colors" data-testid="pricing-footer-home">Home</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-testid="pricing-footer-privacy">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors" data-testid="pricing-footer-terms">Terms</Link>
          </div>
          <p className="text-xs">© {new Date().getFullYear()} RecoverOS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
